/* ==========================================================================
   BRITTEN CUSTOM: the /search results page script (search redesign v3, stage B)
   ==========================================================================

   Added:       14 Oct 2026
   Location:    assets/britten-search.js
   Loaded by:   sections/britten-search.liquid (defer, at the end of the section; /search only)
   Markup:      sections/britten-search.liquid, snippets/britten-search-card.liquid
   Styles:      assets/britten-search.css
   Test:        scripts/search-page-test.js runs THIS file in a sandbox (the pure parts, window.BHSearchPage)

   THE PAGE WORKS WITHOUT THIS SCRIPT. The Liquid renders the input, the Type filter, "Products . n", the sort, the
   grid, the numbered page links and the no-results state. This script adds:
     1. Show more: the next page by the Section Rendering API (?section_id=britten-search&page=n, the page's own
        q / type / sort / filter params), its cards appended, ?page= kept with history.replaceState. If the fetch
        fails it goes to the plain page link instead.
     2. The lead (page 1, sort relevance only): the card BHSearch's lead rule picks for what she typed moves out of the
        grid into a larger row (260px photo on desktop, 128px on phones). n stays the same.
     3. The left column: Shop rows (BHSearch's menu match on the drawer), the Help row beside a product word (intent
        "mixed"), and, on desktop, up to two Guides from ONE fetch of
        {market}/search?q=..&type=page&section_id=britten-search-guides, real guides only (BHSearch.isGuide). The Order samples tile (Liquid) is
        dropped when one of those rows already goes to the same place. No Guides on phones.
     3b. A SERVICE-ONLY query (BHSearch's intent "service": returns, my order, contact; the sheet's D5 / P5): the Help rows
        are the whole answer, so they move to the MAIN column as a list (label, rows with sub-lines, the live workshop line
        on Contact, an arrow on desktop) and are not also in the left column. The products area (the lead, "Products . n"
        and the sort, the grid, Show more, the page links) and the Type filter are put behind ONE quiet text link under the
        list, "Show n products for 'q'", a <button> with aria-expanded / aria-controls; it reveals them in place (no
        reload) and moves focus to the products heading. The left column keeps its Guides and Order samples. With no help
        rows to show, or no words for the link (data-bhs-s-show-products-*), the page is as in 3.
     4. An old /search?q= link with no type= is replaced with &type=product added, so the page lists products only.
     5. Small things: Clear, the sort select submitting on change, an empty Enter staying put, the sheet's aliases on
        the page's own submit ("isabella" lands on real results), the placeholder per width, the live workshop line on
        the Contact rows, the Popular list for no results, search_click analytics.

   WHAT IT NEEDS FROM THE HEADER (assets/britten-header.js, loaded before this on every page; this file is read-only
   about it): window.BHSearch (expandQuery, buildModel, readMenu, parseList, pathOf, fold, statusLine, WORD_ATTRS),
   the drawer's markup ([data-bh-mm]) and the header root's data-bh-* strings. With the old header (no BHSearch) the
   script waits about three seconds, then leaves parts 2 and 3 undone: Show more, the type redirect, Clear, the sort
   and the rest of part 5 need nothing from the header and run at once.
   NOT EXPOSED BY THE HEADER, so rebuilt here: the ctx the header builds for buildModel (lang, menu, words, help
   sub-lines: a dozen lines), the near-duplicate key for guides (guideKey), the workshop's open / closed line
   (updateWorkshop) and bhTrack (analytics: gtag, dataLayer, Shopify analytics, same shape).

   ANALYTICS: search_click { kind: lead | product | shop | help | guide, position (1-based inside its kind),
   surface: 'page' }, one event per click on a result link. No query text is sent.

   CHANGELOG
   - 14 Oct 2026: created (search redesign v3, stage B).
   - 14 Oct 2026: a service-only query (intent "service") shows the Help rows as the main column and hides the products
     behind a "Show n products for 'q'" link (3b); mixed and plain queries are unchanged.
   ========================================================================== */
(function () {
  'use strict';

  var DEBUG = false;
  var DESKTOP_QUERY = '(min-width: 1024px)';
  var GUIDES_MAX = 2;            // guides shown in the left column
  var WS_TZ = 'Europe/London';   // the workshop's clock: the header's rule (assets/britten-header.js, updateWorkshop)
  var WS_FROM = 9 * 60;
  var WS_TO = 16 * 60;
  var BH_WAIT_MS = 80;           // waiting for window.BHSearch: 40 x 80ms, about 3 seconds
  var BH_WAIT_TRIES = 40;

  function log(msg) {
    if (DEBUG && window.console) console.log('[Britten search page] ' + msg);
  }
  function toArray(list) { return Array.prototype.slice.call(list || []); }

  /* -----------------------------------------------------------------------
     THE PURE PARTS (no DOM). scripts/search-page-test.js drives these through window.BHSearchPage.
     ----------------------------------------------------------------------- */

  // An old link, /search?q=sandy, has no type: Shopify then returns products, pages and articles together. Return the
  // same URL with &type=product added, or null when there is nothing to do (no q, a type already, not the search page).
  // loc: { pathname, search, hash }
  function noTypeRedirectUrl(loc) {
    var search = String((loc && loc.search) || '');
    if (!/\/search\/?$/.test(String((loc && loc.pathname) || ''))) return null;
    if (!/[?&]q=/.test(search) || /[?&]type=/.test(search)) return null;
    return loc.pathname + search + '&type=product' + String(loc.hash || '');
  }

  function pageOf(search) {
    var m = /[?&]page=(\d+)/.exec(String(search || ''));
    return m ? Math.max(1, parseInt(m[1], 10)) : 1;
  }

  // The lead only belongs on page 1 in the relevance order of a products search (a sort by price, or page 3, is not
  // "the closest match first"). sort: the page's sort_by.
  function leadAllowed(search, mode, sort) {
    return mode === 'products' && pageOf(search) === 1 && (!sort || sort === 'relevance');
  }

  // The same page with ?page=n (page 1 drops the parameter): what history.replaceState writes after Show more.
  function pageUrl(loc, page) {
    var p = new URLSearchParams(String((loc && loc.search) || ''));
    if (page > 1) p.set('page', String(page));
    else p.delete('page');
    var qs = p.toString();
    return loc.pathname + (qs ? '?' + qs : '') + String(loc.hash || '');
  }

  // The Section Rendering request for the next page: this page's own query (q, type, sort_by, filter.*) with page=n
  // and section_id=<this section>. Nothing from the old page=? survives.
  function showMoreUrl(loc, page, sectionId) {
    var p = new URLSearchParams(String((loc && loc.search) || ''));
    p.set('page', String(page));
    p.set('section_id', sectionId);
    return loc.pathname + '?' + p.toString();
  }

  // The guides: one search of pages, as a section render. q is whatever the page searched for.
  function guidesFetchUrl(searchUrl, q, sectionId) {
    return String(searchUrl || '/search') + '?q=' + encodeURIComponent(q) + '&type=page&section_id=' + encodeURIComponent(sectionId);
  }

  // BHSearch's guideKey: the label folded, punctuation out, words of two characters or fewer out ("What is a Cathedral Length
  // Veil?" and "What is Cathedral Length Veil?" are one guide). BHSearch does not expose it, so it is rebuilt here from
  // BHSearch.fold, the same way (assets/britten-header.js, guideKey and tokens). A label with no word left keeps its folded text.
  function guideKey(BH, label) {
    var key = BH.fold(label).split(/[^a-z0-9\u0100-\uffff]+/).filter(function (t) { return t.length > 2; }).join(' ');
    return key || BH.fold(label);
  }

  // items: [{ label, href }] in the order Shopify gave them. Up to `max` rows; a page that is not a real guide (BHSearch.isGuide),
  // one whose title does not name what she typed (named, from BHSearch.guideNamer; Shopify matches the body too), a guide at a
  // path already taken, or one whose key matches one already taken, is dropped (Shopify can hold two pages that differ by an article).
  function collapseGuides(BH, items, max, named) {
    var rows = [];
    var seenPath = {};
    var seenKey = {};
    (items || []).forEach(function (g) {
      if (rows.length >= max || !g || !g.label || !g.href || !BH.isGuide(g.href) || (named && !named(g.label))) return;
      var path = BH.pathOf(g.href);
      var key = guideKey(BH, g.label);
      if (!path || seenPath[path] || seenKey[key]) return;
      seenPath[path] = true;
      seenKey[key] = true;
      rows.push({ label: String(g.label), href: g.href });
    });
    return rows;
  }

  // The page's cards as the response BHSearch.buildModel reads (a predictive-search response): so the lead, the shop rows
  // and the help rows are BHSearch's own answer for this query, not a second opinion.
  // cards: [{ title, url }]
  function cardsToResponse(cards) {
    return {
      resources: {
        results: {
          products: (cards || []).map(function (c) { return { title: c.title, url: c.url, handle: '' }; }),
          collections: [],
          pages: []
        }
      }
    };
  }

  // -> { model: BHSearch's model for this query, leadAt: the index in `cards` of the lead, or -1 }
  function analyse(BH, ctx, q, cards, wantLead) {
    var model = BH.buildModel(cardsToResponse(cards), q, ctx);
    var leadAt = -1;
    if (wantLead && model.lead) {
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].url === model.lead.url) { leadAt = i; break; }
      }
    }
    return { model: model, leadAt: leadAt };
  }

  // The workshop's open / closed line: assets/britten-header.js updateWorkshop(), as a function of the clock.
  // Open 09:00 to 16:00 Europe/London, Monday to Friday, not on a closed date (the footer card's data-bf-closed).
  // ws: { open, today, next, tomorrow, on, locale } the words (data-bh-ws-* on the drawer's Contact row).
  // -> { text, state: 'open' | 'closed' } or null when there is not enough to say.
  function workshopStatus(now, ws, closedAttr) {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return null;
    var p = {};
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: WS_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
        .formatToParts(now).forEach(function (x) { p[x.type] = x.value; });
    } catch (err) { return null; }
    var midnight = Date.UTC(+p.year, +p.month - 1, +p.day);
    var minutes = ((+p.hour) % 24) * 60 + (+p.minute);
    if (isNaN(midnight) || isNaN(minutes)) return null;
    var closed = String(closedAttr || '').match(/\d{4}-\d{2}-\d{2}/g) || [];
    function working(i) {
      var d = new Date(midnight + i * 864e5);
      var dow = d.getUTCDay();
      return dow >= 1 && dow <= 5 && closed.indexOf(d.toISOString().slice(0, 10)) === -1;
    }
    var isOpen = working(0) && minutes >= WS_FROM && minutes < WS_TO;
    var text = '';
    if (isOpen) {
      text = ws.open || '';
    } else if (working(0) && minutes < WS_FROM) {
      text = ws.today || '';
    } else {
      var k = 1;
      while (k < 15 && !working(k)) k++;
      var when = ws.tomorrow || '';
      if (k > 1) {
        var day = '';
        try {
          day = new Intl.DateTimeFormat(ws.locale || undefined, { weekday: 'long', timeZone: 'UTC' }).format(midnight + k * 864e5);
        } catch (err2) { day = ''; }
        when = day ? String(ws.on || '').replace('{day}', day) : '';
      }
      text = when ? String(ws.next || '').replace('{when}', when) : '';
    }
    if (!text) return null;
    return { text: text, state: isOpen ? 'open' : 'closed' };
  }

  // A SERVICE-ONLY query: BHSearch reads it as a service question ("returns", "my order") and has help rows to show. Only then
  // are the help rows the page's main column and the products put behind a link. "mixed" ("silk veil delivery"), "verb", "guide"
  // and "plain" keep today's page, and so does a model with no help rows (the page has nothing to put in their place).
  function isServiceOnly(model, mode) {
    return mode === 'products' && !!model && model.intent === 'service' && !!model.help && model.help.length > 0;
  }

  // The products count out of the heading Liquid printed ("Products . 14"): the first run of digits, else 0.
  function countFrom(text) {
    var m = /\d+/.exec(String(text == null ? '' : text));
    return m ? parseInt(m[0], 10) : 0;
  }

  // The reveal link's words: one / other template (the header's status line does the same), its n and q placeholders filled in.
  // Function replacers, so a "$&" in what she typed is just text. '' when there is no template (the page then stays as it was).
  function showProductsLabel(tplOne, tplOther, n, q) {
    var tpl = n === 1 ? tplOne : tplOther;
    if (!tpl) return '';
    return String(tpl)
      .replace(/\{\{\s*n\s*\}\}/g, function () { return String(n); })
      .replace(/\{\{\s*q\s*\}\}/g, function () { return String(q == null ? '' : q); });
  }

  // Analytics, the header's bhTrack: gtag, dataLayer and Shopify's analytics bus, each only if present.
  function track(name, payload) {
    payload = payload || {};
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
      var ev = { event: name };
      Object.keys(payload).forEach(function (k) { ev[k] = payload[k]; });
      (window.dataLayer = window.dataLayer || []).push(ev);
    } catch (e) { /* analytics must never break the page */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(name, payload);
    } catch (e2) { /* same */ }
  }

  window.BHSearchPage = {
    noTypeRedirectUrl: noTypeRedirectUrl, leadAllowed: leadAllowed, pageOf: pageOf, pageUrl: pageUrl, showMoreUrl: showMoreUrl,
    guidesFetchUrl: guidesFetchUrl, guideKey: guideKey, collapseGuides: collapseGuides,
    cardsToResponse: cardsToResponse, analyse: analyse, workshopStatus: workshopStatus, track: track,
    isServiceOnly: isServiceOnly, countFrom: countFrom, showProductsLabel: showProductsLabel
  };

  /* -----------------------------------------------------------------------
     THE PAGE
     ----------------------------------------------------------------------- */
  function make(tag, cls, text) {
    var el = document.createElement(tag);
    if (cls) el.className = cls;
    if (text != null) el.textContent = text;
    return el;
  }
  function sized(u, w) {
    if (typeof u !== 'string' || !u) return '';
    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function init() {
    var root = document.querySelector('[data-bhs]');
    if (!root || root.__bhsReady) return;
    root.__bhsReady = true;

    // 4. an old link with no type: say products, once. location.replace, so Back does not return to the typeless URL.
    var redirect = noTypeRedirectUrl(window.location);
    if (redirect) {
      window.location.replace(redirect);
      return;
    }
    root.classList.add('bhs--js');

    var header = document.querySelector('[data-bh-header]');
    var mode = root.getAttribute('data-bhs-mode') || 'products';
    var q = root.getAttribute('data-bhs-q') || '';
    var sort = root.getAttribute('data-bhs-sort') || 'relevance';
    var searchUrl = root.getAttribute('data-bhs-search-url') || '/search';
    var sectionId = root.getAttribute('data-bhs-section') || 'britten-search';
    var guidesSection = root.getAttribute('data-bhs-guides-section') || 'britten-search-guides';
    var mqDesktop = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
    function isDesktop() { return mqDesktop ? mqDesktop.matches : true; }
    function headAttr(name) { return (header && header.getAttribute(name)) || ''; }

    var input = root.querySelector('[data-bhs-input]');
    var clear = root.querySelector('[data-bhs-clear]');
    var form = root.querySelector('[data-bh-page-form]');
    var grid = root.querySelector('[data-bhs-grid]');
    var ctx = root.querySelector('[data-bhs-ctx]');
    var leadEl = root.querySelector('[data-bhs-lead]');
    var statusEl = root.querySelector('[data-bhs-status]');
    var more = root.querySelector('[data-bhs-more]');
    var moreBtn = root.querySelector('[data-bhs-more-btn]');

    /* ---- 5. the input: Clear, the placeholder per width, empty Enter stays, aliases on submit ---- */
    function syncClear() { if (clear && input) clear.hidden = !input.value; }
    function setPlaceholder() {
      if (!input) return;
      var ph = input.getAttribute(isDesktop() ? 'data-bhs-ph-desktop' : 'data-bhs-ph-mobile');
      if (ph) input.setAttribute('placeholder', ph);
    }
    if (input) {
      syncClear();
      setPlaceholder();
      input.addEventListener('input', syncClear);
      if (clear) {
        clear.addEventListener('click', function () {
          input.value = '';
          syncClear();
          input.focus();
        });
      }
    }
    if (mqDesktop) {
      if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', setPlaceholder);
      else if (mqDesktop.addListener) mqDesktop.addListener(setPlaceholder);
    }
    if (form && input) {
      form.addEventListener('submit', function (e) {
        var typed = input.value.trim();
        if (!typed) {
          e.preventDefault();
          input.focus();
          return;
        }
        // the sheet sends the aliased query ("isabella" -> "single tier cut edge veil"); the page's own box does the same
        if (window.BHSearch && window.BHSearch.expandQuery) {
          var sent = window.BHSearch.expandQuery(typed);
          if (sent !== typed.toLowerCase()) input.value = sent;
        }
      });
    }

    /* ---- the sort select submits on change (no script: a button in <noscript>) ---- */
    var sortSel = root.querySelector('[data-bhs-sort]');
    var sortForm = root.querySelector('[data-bhs-sort-form]');
    if (sortSel && sortForm) sortSel.addEventListener('change', function () { sortForm.submit(); });
    // a select is as wide as its widest option; fit it to the chosen one (the chevron sits at its right edge)
    function fitSort() {
      var opt = sortSel && sortSel.options[sortSel.selectedIndex];
      if (!opt || !window.getComputedStyle) return;
      var cs = window.getComputedStyle(sortSel);
      var probe = make('span', null, opt.text);
      probe.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font-family:' + cs.fontFamily + ';font-size:' + cs.fontSize +
        ';font-weight:' + cs.fontWeight + ';letter-spacing:' + cs.letterSpacing;
      document.body.appendChild(probe);
      var w = probe.getBoundingClientRect().width;
      document.body.removeChild(probe);
      if (w > 0) sortSel.style.width = Math.ceil(w + 20) + 'px';
    }
    if (sortSel) {
      fitSort();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitSort);
    }

    /* ---- Type tabs (below 1280px): the active tab scrolled into view ---- */
    var typeList = root.querySelector('.bhs-type__list');
    var typeActive = typeList ? typeList.querySelector('[aria-current]') : null;
    if (typeList && typeActive && typeList.scrollWidth > typeList.clientWidth) {
      typeList.scrollLeft = Math.max(0, typeActive.offsetLeft - (typeList.clientWidth - typeActive.offsetWidth) / 2);
    }

    /* ---- analytics: one search_click per result link ---- */
    root.addEventListener('click', function (e) {
      var k = e.target && e.target.closest ? e.target.closest('[data-bhs-k]') : null;
      if (k) track('search_click', { kind: k.getAttribute('data-bhs-k'), position: parseInt(k.getAttribute('data-bhs-pos'), 10) || 1, surface: 'page' });
    });

    /* ---- 1. Show more ---- */
    function statusTemplates() {
      return {
        productOne: headAttr('data-bh-s-status-product-one'),
        productOther: headAttr('data-bh-s-status-product-other')
      };
    }
    var busy = false;
    function setStatus(added) {
      if (!statusEl || !added) return;
      var line = window.BHSearch && window.BHSearch.statusLine ? window.BHSearch.statusLine({ products: added }, statusTemplates()) : '';
      statusEl.textContent = line;
    }
    if (grid && more && moreBtn) {
      var page0 = parseInt(grid.getAttribute('data-bhs-page'), 10) || 1;
      var pages0 = parseInt(grid.getAttribute('data-bhs-pages'), 10) || 1;
      if (window.fetch && window.DOMParser && page0 < pages0) more.hidden = false;
      moreBtn.addEventListener('click', function () {
        if (busy) return;
        var page = parseInt(grid.getAttribute('data-bhs-page'), 10) || 1;
        var pages = parseInt(grid.getAttribute('data-bhs-pages'), 10) || 1;
        if (page >= pages) return;
        var next = page + 1;
        busy = true;
        moreBtn.setAttribute('aria-busy', 'true');
        fetch(showMoreUrl(window.location, next, sectionId), { credentials: 'same-origin' })
          .then(function (r) {
            if (!r.ok) throw new Error('page ' + next + ' ' + r.status);
            return r.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-bhs-grid]');
            if (!fresh || !fresh.children.length) throw new Error('page ' + next + ' has no grid');
            var firstNew = null;
            var added = 0;
            toArray(fresh.children).forEach(function (li) {
              var node = document.importNode(li, true);
              grid.appendChild(node);
              added++;
              if (!firstNew) firstNew = node.querySelector('a');
            });
            var newPages = parseInt(fresh.getAttribute('data-bhs-pages'), 10) || pages;
            grid.setAttribute('data-bhs-page', String(next));
            grid.setAttribute('data-bhs-pages', String(newPages));
            try { window.history.replaceState(window.history.state, '', pageUrl(window.location, next)); } catch (err) { /* no history API: the URL stays */ }
            setStatus(added);
            if (next >= newPages) {
              // the button is about to go: the new cards are where she is reading, so focus goes to the first of them
              more.hidden = true;
              if (firstNew && firstNew.focus) firstNew.focus();
            }
          })
          .catch(function (err) {
            log('show more failed: ' + err);
            window.location.assign(pageUrl(window.location, next));
          })
          .then(function () {
            busy = false;
            moreBtn.removeAttribute('aria-busy');
          });
      });
    }

    /* ---- the rest needs the header's BHSearch ---- */
    var tries = 0;
    function whenBHSearch() {
      if (window.BHSearch && window.BHSearch.buildModel) {
        withBHSearch(window.BHSearch);
        return;
      }
      if (++tries < BH_WAIT_TRIES) setTimeout(whenBHSearch, BH_WAIT_MS);
      else log('BHSearch never arrived (the old header?): the page stays as the Liquid drew it');
    }
    whenBHSearch();

    function withBHSearch(BH) {
      // the workshop's words, from the drawer's Contact row ([data-bh-ws]); the header only keeps them current when the drawer opens
      function workshop() {
        var el = document.querySelector('[data-bh-ws]');
        if (!el) return null;
        var closedEl = document.querySelector('[data-bf-closed]');
        return workshopStatus(new Date(), {
          open: el.getAttribute('data-bh-ws-open') || '',
          today: el.getAttribute('data-bh-ws-today') || '',
          next: el.getAttribute('data-bh-ws-next') || '',
          tomorrow: el.getAttribute('data-bh-ws-tomorrow') || '',
          on: el.getAttribute('data-bh-ws-on') || '',
          locale: el.getAttribute('data-bh-ws-locale') || ''
        }, (closedEl && closedEl.getAttribute('data-bf-closed')) || '');
      }
      function liveRows() {
        var rows = toArray(root.querySelectorAll('[data-bhs-ws-text]'));
        if (!rows.length) return;
        var ws = workshop();
        if (!ws) return;
        rows.forEach(function (out) {
          out.textContent = ws.text;
          var row = out.closest ? out.closest('.bhs-sx') : null;
          if (row) row.setAttribute('data-bhs-ws-state', ws.state);
        });
      }

      // the model ctx the header builds for buildModel (not exposed, so rebuilt: assets/britten-header.js, buildSearchCtx)
      var words = {};
      Object.keys(BH.WORD_ATTRS).forEach(function (k) { words[k] = BH.parseList(headAttr(BH.WORD_ATTRS[k])); });
      var bhCtx = {
        lang: (document.documentElement.getAttribute('lang') || 'en').toLowerCase().slice(0, 2),
        searchUrl: searchUrl,
        menu: BH.readMenu(document.querySelector('[data-bh-mm]')),
        words: words,
        subs: { returns: headAttr('data-bh-s-help-returns-sub'), shipping: headAttr('data-bh-s-help-shipping-sub') }
      };

      // ---- no results: Popular (the header's own list), the Contact row live ----
      var none = root.querySelector('[data-bhs-none]');
      if (none) {
        var popSrc = document.querySelector('[data-bh-popular]');
        var popLinks = popSrc ? toArray(popSrc.querySelectorAll('a')) : [];
        if (popLinks.length) {
          var pop = make('section', 'bhs-pop');
          var ph = make('p', 'bhs-lab bhs-sg__h', (popSrc.querySelector('.bh-sg__h') || {}).textContent || '');
          ph.id = 'bhs-pop-h';
          var ul = make('ul', 'bhs-pop__list');
          ul.setAttribute('aria-labelledby', 'bhs-pop-h');
          popLinks.forEach(function (a) {
            var li = make('li');
            var link = make('a', 'bhs-pop__a', a.textContent);
            link.setAttribute('href', a.getAttribute('href') || '#');
            li.appendChild(link);
            ul.appendChild(li);
          });
          pop.appendChild(ph);
          pop.appendChild(ul);
          var sentence = none.querySelector('.bhs-none__t');
          none.insertBefore(pop, sentence ? sentence.nextSibling : none.firstChild);
        }
      }

      if (mode !== 'products' || !grid || !ctx) {
        liveRows();
        return;
      }

      // ---- what BHSearch makes of this query, over this page's own products ----
      var cardEls = toArray(grid.querySelectorAll('.bhs-card'));
      var cards = cardEls.map(function (a) { return { title: a.getAttribute('data-bhs-title') || '', url: a.getAttribute('href') || '' }; });
      var found = analyse(BH, bhCtx, q, cards, leadAllowed(window.location.search, mode, sort));
      var model = found.model;

      // 2. the lead
      if (found.leadAt >= 0 && leadEl) {
        var card = cardEls[found.leadAt];
        var px = isDesktop() ? 260 : 128;
        var a = make('a', 'bhs-sr bhs-sr--lead');
        a.setAttribute('href', card.getAttribute('href'));
        a.setAttribute('data-bhs-k', 'lead');
        a.setAttribute('data-bhs-pos', '1');
        var box = make('span', 'bhs-sr__img');
        var base = card.getAttribute('data-bhs-img');
        if (base) {
          var img = make('img');
          img.setAttribute('src', sized(base, px * 2));
          img.setAttribute('alt', '');   // decorative: the name is the row's own text
          img.setAttribute('width', String(px));
          img.setAttribute('height', String(px));
          img.setAttribute('loading', 'eager');
          img.setAttribute('decoding', 'async');
          box.appendChild(img);
        }
        a.appendChild(box);
        var body = make('span', 'bhs-sr__b');
        function copy(sel, cls) {
          var src = card.querySelector(sel);
          if (src && src.textContent) body.appendChild(make('span', cls, src.textContent));
        }
        copy('.bhs-card__t', 'bhs-sr__t');
        copy('.bhs-card__s', 'bhs-sr__s');
        copy('.bhs-card__p', 'bhs-sr__p');
        a.appendChild(body);
        leadEl.appendChild(a);
        leadEl.hidden = false;
        var cell = card.parentNode;
        if (cell && cell.parentNode) cell.parentNode.removeChild(cell);   // n stays what Shopify said: the lead is one of them
      }

      // 3. the left column: groups before the Order samples tile
      var samples = ctx.querySelector('[data-bhs-samples]');
      var groupId = 0;
      function group(kind, label, n) {
        var sec = make('section', 'bhs-sg bhs-sg--' + kind);
        var h = make('p', 'bhs-lab bhs-sg__h', label);
        h.id = 'bhs-g-' + kind + '-h' + (++groupId);
        sec.appendChild(h);
        var ul = make('ul', 'bhs-sg__list');
        ul.setAttribute('aria-labelledby', h.id);
        sec.appendChild(ul);
        sec.style.setProperty('--n', String(n));
        ctx.insertBefore(sec, samples);
        return ul;
      }
      function item(ul, node) {
        var li = make('li');
        li.appendChild(node);
        ul.appendChild(li);
      }
      function mark(a, kind, pos) {
        a.setAttribute('data-bhs-k', kind);
        a.setAttribute('data-bhs-pos', String(pos));
        return a;
      }
      var shown = {};   // paths a row above already goes to: the Order samples tile is not drawn twice

      // one help row: label, sub-line (the Contact row's is the live workshop line with its dot), an arrow CSS shows where it fits
      function helpRow(r, i) {
        var a2 = mark(make('a', 'bhs-sx bhs-sx--help'), 'help', i + 1);
        a2.setAttribute('href', r.href);
        var text = make('span', 'bhs-sx__x');
        text.appendChild(make('span', 'bhs-sx__t', r.label));
        var sub = r.sub || '';
        if (r.live) {
          var live = make('span', 'bhs-sx__s bhs-sx__s--live');
          var dot = make('span', 'bhs-dot');
          dot.setAttribute('aria-hidden', 'true');
          live.appendChild(dot);
          var liveText = make('span', null, sub);
          liveText.setAttribute('data-bhs-ws-text', '');
          live.appendChild(liveText);
          text.appendChild(live);
        } else if (sub) {
          text.appendChild(make('span', 'bhs-sx__s', sub));
        }
        a2.appendChild(text);
        var arrow = make('span', 'bhs-sx__arrow', '\u2192');
        arrow.setAttribute('aria-hidden', 'true');
        a2.appendChild(arrow);
        shown[BH.pathOf(r.href)] = true;
        return a2;
      }

      // Help. Beside a product word (intent "mixed") it is the one row in the left column, as ever. A SERVICE-ONLY query
      // (3b) puts the rows in the main column instead and the products behind a link; the left column then has no Help.
      var service = isServiceOnly(model, mode);
      var showLabel = '';
      if (service) {
        var productsHead = root.querySelector('.bhs-ph__h');
        showLabel = showProductsLabel(
          root.getAttribute('data-bhs-s-show-products-one') || '',
          root.getAttribute('data-bhs-s-show-products-other') || '',
          countFrom(productsHead && productsHead.textContent) || grid.querySelectorAll('.bhs-card').length,
          q
        );
        // without the link's words the products could not be got back: keep the page as it was
        if (!showLabel) service = false;
      }
      if (service) {
        serviceLayout(model, productsHead, showLabel);
      } else if (model.help.length) {
        var hul = group('help', headAttr('data-bh-s-group-help'), model.help.length);
        model.help.forEach(function (r, i) { item(hul, helpRow(r, i)); });
      }

      // 3b. the Help list as the main column, the products area (and the Type filter) folded away behind one text link
      function serviceLayout(m, heading, label) {
        var main = root.querySelector('.bhs-main');
        if (!main) return;
        var area = make('div', 'bhs-products');
        area.id = 'bhs-products';
        area.hidden = true;
        toArray(main.children).forEach(function (c) { area.appendChild(c); });   // the products line, lead, grid, Show more, page links

        var helpLabel = headAttr('data-bh-s-group-help');
        var sec = make('section', 'bhs-svc');
        var head = make('div', 'bhs-ph bhs-svc__head');
        var h = make('h2', 'bhs-lab bhs-ph__h', helpLabel);
        h.id = 'bhs-svc-h';
        head.appendChild(h);
        sec.appendChild(head);
        var ul = make('ul', 'bhs-svc__list');
        ul.setAttribute('aria-labelledby', h.id);
        m.help.forEach(function (r, i) { item(ul, helpRow(r, i)); });
        sec.appendChild(ul);

        var p = make('p', 'bhs-svc__more');
        var btn = make('button', 'bhs-svc__btn', label);
        btn.setAttribute('type', 'button');
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-controls', area.id);
        p.appendChild(btn);

        main.appendChild(sec);
        main.appendChild(p);
        main.appendChild(area);
        root.classList.add('bhs--service');

        // the reveal: no reload. The link goes (the products line is now the heading), focus lands on that heading.
        btn.addEventListener('click', function () {
          btn.setAttribute('aria-expanded', 'true');
          area.hidden = false;
          p.hidden = true;
          root.classList.add('bhs--reveal');
          if (heading) {
            heading.id = heading.id || 'bhs-products-h';
            heading.setAttribute('tabindex', '-1');
            if (heading.focus) heading.focus();
          }
        });
      }

      // Shop: the matching menu links ("Veils . Veil types")
      if (model.shop.length) {
        var shopLabel = headAttr('data-bh-s-group-shop');
        var sul = group('shop', shopLabel, model.shop.length);
        model.shop.forEach(function (r, i) {
          var a3 = mark(make('a', 'bhs-sx bhs-sx--shop'), 'shop', i + 1);
          a3.setAttribute('href', r.href);
          a3.appendChild(make('span', 'bhs-sx__t', r.label));
          // 1024-1279px the tile reads "Shop . Veils . Veil lengths"; elsewhere the line is just "Veils . Veil lengths"
          if (r.sub) {
            var s = make('span', 'bhs-sx__s');
            s.appendChild(make('span', 'bhs-sx__pre', shopLabel + ' \u00b7 '));
            s.appendChild(document.createTextNode(r.sub));
            a3.appendChild(s);
          } else {
            a3.appendChild(make('span', 'bhs-sx__s bhs-sx__s--tile', shopLabel));
          }
          item(sul, a3);
          shown[BH.pathOf(r.href)] = true;
        });
      }

      // Guides: desktop only, one fetch, near-duplicates collapsed; hidden on any failure
      var guidesDone = false;
      function loadGuides() {
        if (guidesDone || !isDesktop() || !window.fetch || !window.DOMParser || !q) return;
        guidesDone = true;
        fetch(guidesFetchUrl(searchUrl, q, guidesSection), { credentials: 'same-origin' })
          .then(function (r) {
            if (!r.ok) throw new Error('guides ' + r.status);
            return r.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var data = doc.querySelector('[data-bhg]');
            if (!data) return;
            var rows = collapseGuides(BH, toArray(data.querySelectorAll('[data-bhg-item]')).map(function (el) {
              return { label: el.getAttribute('data-bhg-title') || '', href: el.getAttribute('data-bhg-url') || '' };
            }), GUIDES_MAX, BH.guideNamer(q, bhCtx));
            if (!rows.length) return;
            var gul = group('guides', headAttr('data-bh-s-group-guides'), rows.length);
            rows.forEach(function (g, i) {
              var a4 = mark(make('a', 'bhs-gl'), 'guide', i + 1);
              a4.setAttribute('href', g.href);
              a4.appendChild(make('span', 'bhs-gl__t', g.label));
              a4.appendChild(make('span', 'bhs-gl__s', headAttr('data-bh-s-guide-one')));
              item(gul, a4);
              shown[BH.pathOf(g.href)] = true;
            });
            dropSamples();
          })
          .catch(function (err) { log('guides failed: ' + err); });
      }
      function dropSamples() {
        if (!samples) return;
        var link = samples.querySelector('a');
        var href = link ? link.getAttribute('href') : '';
        if (href && shown[BH.pathOf(href)] && samples.parentNode) samples.parentNode.removeChild(samples);
      }
      dropSamples();
      loadGuides();
      if (mqDesktop) {
        var onBreak = function () { if (isDesktop()) loadGuides(); };
        if (mqDesktop.addEventListener) mqDesktop.addEventListener('change', onBreak);
        else if (mqDesktop.addListener) mqDesktop.addListener(onBreak);
      }

      liveRows();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
